import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { Link, matchPath, useLocation } from "react-router";
import { Dialog } from "../../shared/components/Dialog";
import { useSessionExpired } from "../../shared/session/expired-context";
import { meQueryKey, testModeSwitchKey } from "../../shared/session/queries";
import { useSession } from "../../shared/session/session-context";
import { useGuideState, useRecordGuide } from "../guides/api";
import { mayAnnounce, publishedReleases, RELEASE_RECORD_ID, seenRelease, unseenReleases, WHATS_NEW_PATH } from "./model";
import { ReleaseList } from "./ReleaseList";
import { RELEASES, type ReleaseNote } from "./releases";
import { WhatsNewContext, type WhatsNew } from "./whatsNew-context";
import { RELEASE_NOTES_LOCALE, whatsNewText } from "./whatsNewText";
import "./whatsNew.css";

/**
 * What's new (ruling of 17 Oct 2026). When a person opens Giraffe and a published release exists that they have not seen, the
 * release notes open by themselves as a dialog, once per new release: only the unseen releases, newest first (a person who has
 * seen nothing yet gets the latest only), with a link to the full /whats-new page. Closing it (Close, Got it, Esc, a click
 * outside, or following a link in it) records the newest release as seen on the server, per person (guide record
 * `release-notes`), so it stays closed on every browser until the next release.
 *
 * It never opens: for an AI agent's account, in role test mode ("View as"), on /whats-new itself (the page records it), over
 * the session-expired dialog, or while Features__GuidedTours is off (the per-person record lives behind that flag). The portal
 * and the login page are outside the shell, so it is never there. The guided tours hold back while it is due or was shown in
 * this page load (`holdTours`), so the release dialog comes first and the tour prompt waits for the next page load.
 *
 * `releases` is for tests; the app always uses RELEASES.
 */
export function WhatsNewProvider({ children, releases = RELEASES }: { children: ReactNode; releases?: readonly ReleaseNote[] }) {
  const session = useSession();
  const location = useLocation();
  const expired = useSessionExpired();
  const flagOn = session.flag("GuidedTours");
  const eligible = flagOn && mayAnnounce(session);
  const state = useGuideState(flagOn);
  const record = useRecordGuide();
  // The same settling rule as the guided tours (R8-D01): nothing is decided while a test-mode switch or a session refetch is in
  // flight, so the guide state read always belongs to the identity on screen.
  const switching = useIsMutating({ mutationKey: testModeSwitchKey }) > 0;
  const sessionFetching = useIsFetching({ queryKey: meQueryKey, exact: true }) > 0;
  const settled = !switching && !sessionFetching && !state.isFetching;

  const published = useMemo(() => publishedReleases(releases), [releases]);
  const latest = published[0];
  // Seen in this page load, so a failed save never reopens the dialog in a loop (it reopens on the next load instead).
  const [seenHere, setSeenHere] = useState<number | undefined>(undefined);
  const stored = seenRelease(state.data?.tours);
  const seen = stored === undefined ? seenHere : Math.max(stored, seenHere ?? 0);

  const known = eligible && state.isSuccess && settled;
  // Decided: we know whether anything is due (or nothing can be: flag off, not eligible, or the state could not be read).
  const decided = !eligible || state.isError || known;
  const unseen = known && latest ? unseenReleases(published, seen) : [];
  const onPage = matchPath({ path: WHATS_NEW_PATH, end: true }, location.pathname) !== null;
  // Due: something to announce here. Open: due and nothing else modal in the way (the session-expired dialog). The guided
  // tours hold while it is due, not only while it is open, so a tour prompt never slips in under an expired session and is
  // then covered by the release dialog after the sign-in.
  const due = unseen.length > 0 && !onPage;
  const open = due && !expired;
  // Sticky for this page load: once the dialog has opened, the guided tours wait for the next load.
  const [shownHere, setShownHere] = useState(false);
  if (open && !shownHere) setShownHere(true);

  const markSeen = useCallback(() => {
    if (!eligible || !latest) return;
    setSeenHere(latest.batch);
    if ((stored ?? 0) < latest.batch) record.mutate({ tourId: RELEASE_RECORD_ID, version: latest.batch, status: "seen" });
  }, [eligible, latest, stored, record]);

  const value = useMemo<WhatsNew>(
    () => ({
      published,
      showDot: known && latest !== undefined && (seen === undefined || seen < latest.batch),
      markSeen,
      holdTours: eligible && (!decided || due || shownHere),
    }),
    [published, known, latest, seen, markSeen, eligible, decided, due, shownHere],
  );

  return (
    <WhatsNewContext value={value}>
      {children}
      {open && <ReleaseDialog releases={unseen} onClose={markSeen} />}
    </WhatsNewContext>
  );
}

function ReleaseDialog({ releases, onClose }: { releases: readonly ReleaseNote[]; onClose: () => void }) {
  // Thai for everyone, whatever the session locale (ruling of 18 Oct 2026).
  const locale = RELEASE_NOTES_LOCALE;
  return (
    <Dialog
      open
      wide
      title={
        // The title is the dialog's accessible name, so it is announced when the dialog opens.
        <span lang={locale} data-chrome-exempt="release-notes">
          {whatsNewText("dialogTitle", locale)}
        </span>
      }
      onClose={onClose}
      footer={
        <span className="gf-whatsnew-dialog-foot" lang={locale} data-chrome-exempt="release-notes">
          <Link className="btn sec" to={WHATS_NEW_PATH} onClick={onClose}>
            {whatsNewText("allUpdates", locale)}
          </Link>{" "}
          <button type="button" className="btn" data-autofocus onClick={onClose}>
            {whatsNewText("gotIt", locale)}
          </button>
        </span>
      }
    >
      {/* Release notes in Thai for everyone (ruling of 18 Oct 2026): exempt from the English-only chrome checks. */}
      <div className="gf-whatsnew-dialog" lang={locale} data-chrome-exempt="release-notes">
        <p className="gf-whatsnew-dialog-intro">{whatsNewText("dialogIntro", locale)}</p>
        <ReleaseList releases={releases} locale={locale} level={3} onFollowLink={onClose} />
      </div>
    </Dialog>
  );
}
